import type { OperatorFunction } from 'rxjs';

import type { DataTableColumn, DataTableRow } from '@core/user-interface-legacy';

import { ReportResourceType } from './consts.types';
import type { ReportDashboardMetricOptions } from './metrics.types';
import type {
  ReportVisualizationDataResponse,
  ReportDashboardComponentMetaResponse,
  ReportDashboardComponentViewData,
} from './report-component.types';
import { enumValueGuardFactory, reverse } from './reverse-enum';
import { ReportsChartOptions } from './visualization-chart-options.types';

export const NONE_VALUE = 'none';
export const EMPTY_VALUE = '__empty__';

export interface DatasetBase {
  readonly checksum: string | undefined;
  readonly metric: ReportDashboardMetricOptions;
  readonly meta?: ReportDashboardComponentMetaResponse;
  readonly parent?: DatasetBase;

  isSingleDimension: boolean;
  dimensions: Dimension[];
  dataset: {
    dimensions: Dimension[];
    source: Array<['string', ...Array<number | string>]>;
  };

  readonly datasetSize: {
    cols: number;
    rows: number;
  };

  resourceType?: ReportResourceType;
  isEmpty: boolean;
  isInitial: boolean;
  isSingleValue: boolean;

  categoriesMap: Record<string, string>;
  dimensionsMap: Record<string, string>;

  getDataAt: (target: [number, number]) => Dimension | number;

  getRow: (rowIndex: number) => HeaderRow | ValueRow<any>;

  getRawData: () => Data;

  calcTotals: () => any;

  getCategoryName: (index: number) => string;

  getDimensionNameById: (id: string) => string;

  getCategoryNameById: (id: string) => string;

  getIndexDimension: () => Dimension;

  truncateDataset: {
    (dimension: 'first', length: number): DatasetBase;
    (dimension: 'second', length: number, groupIntoOther: false): DatasetBase;
    (dimension: 'second', length: number, groupIntoOther: true, otherLabel: string): DatasetBase;
  };

  filterDataset: (filter: (row: ValueRow<any>) => boolean) => DatasetBase;
}

export type Dimension = {
  id: string | number;
  name: string;
  /**
   * 'category' is only used in funnel reports to apply color coding when 'stage name' group is used.
   * In this case, id/name values are not enough to select proper color, we need 'stage-category', which is system defined enum.
   * In the future we may want to change it to something more generic ('token' ?) if some other scenarios pop up (most probably color coding related)
   */
  category?: string;
};

export type HeaderRow = Dimension[];
export type ValueRow<T> = [Dimension, ...T[]];
export type Data<T = number> = [HeaderRow, ...Array<ValueRow<T>>];

// const headRow: HeaderRow = [
//   { id: 'foo', name: 'foo' },
//   { id: 'bar', name: 'bar' },
// ];
//
// const valRow: ValueRow<number> = [{ id: '1', name: '1' }, 1, 2, 3, 4, 5];
// const data: Data<number> = [headRow, valRow, valRow, valRow];
//
// const dims = data[0];
// const vals = data[1];

export type CellTarget = [number, number];
export type Change<V> = [CellTarget, V];

export const isKnownResourceType = enumValueGuardFactory<typeof ReportResourceType>(
  reverse(ReportResourceType),
);

export type ReportsDataTableRow = DataTableRow & {
  values: Record<
    number | string,
    { id?: number | string; value?: number | string; formatted?: string; name?: string }
  >;
  index: number;
};

export type PivotTableRowValue = Array<{
  id?: number | string;
  value?: number | string;
  formatted?: string;
  name?: string;
  isClickable: boolean;
  // necessary to open details with sorted data
  originalIndex: number;
}>;

export type ReportsDataPivotTableRow = DataTableRow & {
  values: Record<number | string, PivotTableRowValue>;
  index: number;
};

export type ReportDataTableAdditionalColumn = {
  column: DataTableColumn;
  dataFactory: (row: ReportsDataTableRow, index: number) => ReportsDataTableRow;
};

export type TableDataValueRow = [Dimension, ...Array<{ value: number; formatted: string }>];

export type TableData = {
  dimensions: Dimension[];
  values: TableDataValueRow[];
  additionalColumns?: ReportDataTableAdditionalColumn[];
};

export type PivotTableDataValueRow = [
  Dimension,
  Dimension[],
  Array<{ value: number; formatted: string }>,
];

export type PivotTableData = {
  dimensions: [Dimension, Dimension, Dimension];
  values: PivotTableDataValueRow[];
};

interface SummaryDataTotalsMode {
  template: 'total';
}

interface SummaryDataPercentMode {
  template: 'pct';
  a: number;
  b: number;
}

export type SummaryDataItem = {
  header: { primary?: string; secondary?: string };
  dimensions: { primary: Dimension; secondary?: Dimension };
  value: number | string;
  rawValue: number;
  clickable: boolean;
  tooltip?: false | 'funnel-stage-category';
};

export type SummaryData = {
  dimensions: Dimension[];
  values: Array<[Dimension, number]>;
  displayMode?: SummaryDataTotalsMode | SummaryDataPercentMode;
  items: SummaryDataItem[];
  name?: string;
};

export const InitialDataChecksumToken = '__initial__';

export type EmptyData = null | undefined;

export type VisualizationData<T> = {
  vis: T;
  dataset: DatasetBase;
};

export type VisualizationDataTypeUnion =
  | VisualizationData<ReportsChartOptions>
  | VisualizationData<TableData>
  | VisualizationData<SummaryData>
  | VisualizationData<EmptyData>
  | VisualizationData<PivotTableData>;

export type VisualizationOptions = {
  truncateDataset?:
    | number
    // Meta should be totally removed, but that requires vis lib refactor that
    // deprecates visualization pipe in favor of functional config building
    | ((component: ReportDashboardComponentViewData, dataset: DatasetBase) => number);
};

export type VisualizationInputMeta = {} & VisualizationOptions;

export type VisualizationMapperOperatorInput = {
  component: ReportDashboardComponentViewData;
  dataset: DatasetBase;
  metric: ReportDashboardMetricOptions;
  meta: VisualizationInputMeta;
};

export type VisualizationMapperOperator = OperatorFunction<
  VisualizationMapperOperatorInput,
  VisualizationData<any>
>;

export type VisualizeOperatorInput = {
  component: ReportDashboardComponentViewData;
  data: ReportVisualizationDataResponse;
  metric: ReportDashboardMetricOptions;
  meta: VisualizationInputMeta;
};

export type VisualizeOperator = OperatorFunction<VisualizeOperatorInput, VisualizationData<any>>;

// supported visualization rendering methods
export enum VisualizationType {
  Chart = 'chart',
  Summary = 'summary',
  Table = 'table',
  PivotTable = 'pivot-table',
}

type BlueprintMatchFn = (
  component: ReportDashboardComponentViewData,
  meta: VisualizationInputMeta,
) => boolean;

export type Blueprint<T> = {
  match: Partial<ReportDashboardComponentViewData> | BlueprintMatchFn;
  factory: (params: VisualizationMapperOperatorInput) => VisualizationData<T>;
};

export const MAX_SECOND_DIMENSION_LENGTH = 12;

export const OTHER_GROUP_ID = '__other__';

export const PIVOT_TABLE_COUNT_ID = 'count';
